import { useForm } from '@tanstack/react-form'
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { authApi } from '~/entities/session/api/auth'
import { useSupabaseSession, useUpgradeToBusiness } from '~/entities/session/model/use-auth'
import { ConsentFields } from '~/features/auth/auth-form-fields'
import { AuthHeading, AuthShell } from '~/features/auth/auth-shell'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { consentSchema } from '~/shared/types'
import { Button } from '~/shared/ui/button'

export const Route = createFileRoute('/upgrade')({
  component: UpgradePage,
})

function UpgradePage() {
  const session = useSupabaseSession()
  const navigate = useNavigate()
  const upgrade = useUpgradeToBusiness()

  const form = useForm({
    defaultValues: {
      acceptPrivacyPolicy: false,
      acceptTerms: false,
    },
    onSubmit: () => {
      upgrade.mutate()
    },
  })

  if (session === undefined) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="inline-block border-4 border-muted-foreground border-r-transparent border-solid rounded-full w-8 h-8 animate-spin" />
      </div>
    )
  }

  if (session === null) {
    return <Navigate to="/login" />
  }

  const roles = (session.user.app_metadata?.roles as string[] | undefined) ?? ['user']
  if (roles.some((r) => r === 'business_admin' || r === 'business_worker')) {
    return <Navigate to="/dashboard" />
  }

  return (
    <AuthShell
      footer={
        <button
          type="button"
          onClick={async () => {
            await authApi.logout()
            navigate({ to: '/login' })
          }}
          className="text-muted-foreground text-sm transition-colors hover:text-foreground"
        >
          {m.switchAccount()}
        </button>
      }
    >
      <AuthHeading
        title={m.upgradeToBusinessTitle()}
        description={m.upgradeToBusinessDescription()}
      />
      {session.user.email && (
        <p className="squircle -mt-2 truncate rounded-lg bg-muted px-3 py-2 text-muted-foreground text-sm">
          {m.signedInAs()} <span className="font-medium text-foreground">{session.user.email}</span>
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (consentSchema.safeParse(form.state.values).success) form.handleSubmit()
        }}
        className="space-y-4"
      >
        <ConsentFields form={form} />

        {upgrade.isError && (
          <div className="squircle rounded-lg border border-destructive/20 bg-destructive/10 p-3">
            <p className="text-destructive text-sm">{getErrorMessageFromError(upgrade.error)}</p>
          </div>
        )}

        <form.Subscribe selector={(state) => state.values}>
          {(values) => (
            <Button
              type="submit"
              variant="brand"
              size="lg"
              className="w-full"
              disabled={!consentSchema.safeParse(values).success || upgrade.isPending}
            >
              {upgrade.isPending ? (
                <>
                  <Loader2 className="animate-spin" />
                  {m.upgrading()}
                </>
              ) : (
                m.upgradeAccount()
              )}
            </Button>
          )}
        </form.Subscribe>
      </form>
    </AuthShell>
  )
}
